import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  Paper,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  useTheme,
  alpha,
  Tooltip
} from '@mui/material';
import ShipIcon from '@mui/icons-material/DirectionsBoat';
import ExploreIcon from '@mui/icons-material/Explore';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptIcon from '@mui/icons-material/Receipt';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SiteWrapper from '../../home-pages/SiteWrapper';

// Ships configuration
const cruiseSpecs = {
  icon: {
    key: 'icon',
    name: "Icon of the Seas",
    desc: "World's Ultimate Family Vacation",
    homeport: "Miami, FL",
    dates: "June 26 – July 3, 2027 (Sat–Sat)",
    highlight: "Category 6 Waterpark & Surfside Neighborhood",
    photo: "https://images.unsplash.com/photo-1548574505-5e239809ee19?auto=format&fit=crop&q=80&w=800",
    rates: {
      interior: 2000,
      oceanview: 2250,
      balcony: 2450,
      suite: 4000
    },
    ports: [
      { day: "Day 1", port: "Miami, FL", action: "Embarkation (Sails 4:30 PM)" },
      { day: "Day 2", port: "Cruising", action: "At Sea - Explore Thrill Island" },
      { day: "Day 3", port: "Cruising", action: "At Sea - Relax in Chill Island" },
      { day: "Day 4", port: "Philipsburg, St. Maarten", action: "Docked (8:00 AM - 6:00 PM)", highlight: true },
      { day: "Day 5", port: "Charlotte Amalie, St. Thomas", action: "Docked (7:00 AM - 4:00 PM)", highlight: true },
      { day: "Day 6", port: "Cruising", action: "At Sea - Broadway show night" },
      { day: "Day 7", port: "Perfect Day at CocoCay, Bahamas", action: "Docked (8:00 AM - 5:00 PM)", highlight: true },
      { day: "Day 8", port: "Miami, FL", action: "Debarkation (Arrival 6:00 AM)" }
    ]
  },
  star: {
    key: 'star',
    name: "Star of the Seas",
    desc: "Royal Caribbean's Newest Icon-Class Icon",
    homeport: "Port Canaveral, FL (Orlando)",
    dates: "June 27 – July 4, 2027 (Sun–Sun)",
    highlight: "Brand-New Ship & Perfect Travel Buffer Day",
    photo: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&q=80&w=800",
    rates: {
      interior: 1980,
      oceanview: 2200,
      balcony: 2380,
      suite: 4500
    },
    ports: [
      { day: "Day 1", port: "Port Canaveral, FL", action: "Embarkation (Sails 4:30 PM)" },
      { day: "Day 2", port: "Perfect Day at CocoCay, Bahamas", action: "Docked (9:00 AM - 5:00 PM)", highlight: true },
      { day: "Day 3", port: "Cruising", action: "At Sea - Waterpark Day" },
      { day: "Day 4", port: "Charlotte Amalie, St. Thomas", action: "Docked (8:00 AM - 5:00 PM)", highlight: true },
      { day: "Day 5", port: "Basseterre, St. Kitts & Nevis", action: "Docked (8:00 AM - 5:00 PM)", highlight: true },
      { day: "Day 6", port: "Cruising", action: "At Sea - Surfside Play" },
      { day: "Day 7", port: "Cruising", action: "At Sea - Sunset dinner at AquaDome" },
      { day: "Day 8", port: "Port Canaveral, FL", action: "Debarkation (Arrival 6:00 AM)" }
    ]
  }
};

const flatFees = {
  taxesAndFees: 180,
  gratuities: 126
};

const addonRates = {
  deluxeDrinks: 595,
  sodaDrinks: 105,
  wifi: 175
};

const CruisePlanning = () => {
  const theme = useTheme();
  
  // State
  const [activeShip, setActiveShip] = useState('icon');
  const [voteShip, setVoteShip] = useState('icon');
  const [copied, setCopied] = useState(false);
  const [countdownText, setCountdownText] = useState({ days: '--', hours: '--', mins: '--' });

  const [families, setFamilies] = useState({
    grandparents: {
      name: "Terry & Laraine (Grandparents)",
      attendees: 2,
      adults: 2,
      kids: 0,
      rooms: ['balcony'],
      addons: { drinks: false, soda: false, wifi: true, excursions: true },
      excursionRate: 150,
      flightMode: 'd2p'
    },
    tom: {
      name: "Tom's Family",
      attendees: 7,
      adults: 2,
      kids: 5,
      rooms: ['balcony', 'interior'],
      addons: { drinks: false, soda: true, wifi: false, excursions: false },
      excursionRate: 100,
      flightMode: 'commercial'
    },
    wendy: {
      name: "Wendy's Family",
      attendees: 4,
      adults: 4,
      kids: 0,
      rooms: ['balcony'],
      addons: { drinks: false, soda: false, wifi: true, excursions: true },
      excursionRate: 150,
      flightMode: 'commercial'
    },
    leah: {
      name: "Leah & Ryan's Family",
      attendees: 4,
      adults: 2,
      kids: 2,
      rooms: ['balcony'],
      addons: { drinks: false, soda: true, wifi: true, excursions: true },
      excursionRate: 150,
      flightMode: 'd2'
    }
  });

  // Countdown timer effect
  useEffect(() => {
    const targetDate = new Date("June 26, 2027 16:30:00").getTime();
    
    const updateTimer = () => {
      const now = new Date().getTime();
      const diff = targetDate - now;

      if (diff <= 0) {
        setCountdownText({ days: '00', hours: '00', mins: '00' });
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      setCountdownText({
        days: String(days).padStart(2, '0'),
        hours: String(hours).padStart(2, '0'),
        mins: String(mins).padStart(2, '0')
      });
    };

    updateTimer();
    const interval = setInterval(updateTimer, 60000);
    return () => clearInterval(interval);
  }, []);

  // Room occupancies helper
  const getRoomOccupancies = (famKey) => {
    if (famKey === 'grandparents') return [2];
    if (famKey === 'tom') return [4, 3];
    if (famKey === 'wendy') return [4];
    if (famKey === 'leah') return [4];
    return [1];
  };

  // Flight cost lookup
  const getFlightCost = (mode) => {
    if (mode === 'none') return 0;
    if (mode === 'commercial') return 350;
    if (mode === 'd2') return 0;
    if (mode === 'd2p') return 50;
    if (mode === 'd3') return 100;
    return 0;
  };

  // Event handlers
  const handleRoomChange = (famKey, roomIdx, value) => {
    setFamilies(prev => {
      const copy = { ...prev };
      const updatedRooms = [...copy[famKey].rooms];
      updatedRooms[roomIdx] = value;
      copy[famKey] = { ...copy[famKey], rooms: updatedRooms };
      return copy;
    });
  };

  const handleAddonToggle = (famKey, addonKey, checked) => {
    setFamilies(prev => {
      const copy = { ...prev };
      copy[famKey] = {
        ...copy[famKey],
        addons: { ...copy[famKey].addons, [addonKey]: checked }
      };
      return copy;
    });
  };

  const handleFlightModeChange = (famKey, value) => {
    setFamilies(prev => {
      const copy = { ...prev };
      copy[famKey] = { ...copy[famKey], flightMode: value };
      return copy;
    });
  };

  // Cost calculators
  const rates = cruiseSpecs[activeShip].rates;
  let grandTotal = 0;
  let totalAttendees = 0;
  const familyTotals = {};

  Object.keys(families).forEach(famKey => {
    const fam = families[famKey];
    let famSum = 0;
    const occupancies = getRoomOccupancies(famKey);

    // 1. Room Base Rate
    fam.rooms.forEach((roomType, idx) => {
      const occ = occupancies[idx] || 2;
      const baseRate = rates[roomType] || rates.interior;
      const roomCost = (baseRate * Math.min(2, occ)) + (occ > 2 ? (occ - 2) * 800 : 0);
      famSum += roomCost;
    });

    // 2. Fees
    famSum += (flatFees.taxesAndFees + flatFees.gratuities) * fam.attendees;

    // 3. Drinks
    if (fam.addons.drinks) famSum += addonRates.deluxeDrinks * fam.adults;
    if (fam.addons.soda && fam.kids > 0) famSum += addonRates.sodaDrinks * fam.kids;

    // 4. Wi-Fi
    if (fam.addons.wifi) {
      const dev = famKey === 'tom' ? 4 : 2;
      famSum += addonRates.wifi * dev;
    }

    // 5. Excursions
    if (fam.addons.excursions) famSum += fam.excursionRate * fam.attendees;

    // 6. Flights
    famSum += getFlightCost(fam.flightMode) * fam.attendees;

    familyTotals[famKey] = famSum;
    grandTotal += famSum;
    totalAttendees += fam.attendees;
  });

  const colors = {
    grandparents: '#8b5cf6',
    tom: '#3b82f6',
    wendy: '#10b981',
    leah: '#f59e0b'
  };

  // Clipboard copy
  const copyPlanToClipboard = () => {
    const spec = cruiseSpecs[activeShip];
    const voted = cruiseSpecs[voteShip].name;

    let text = `🚢 DYMOCK FAMILY CRUISE 2027 PLANNING SUMMARY 🚢\n`;
    text += `==========================================\n`;
    text += `Date Week: June 26 – July 4, 2027\n`;
    text += `Selected Comparison Ship: ${spec.name} (${spec.homeport})\n`;
    text += `Voted Preferred Ship: ${voted}\n\n`;

    text += `FAMILY GROUP STATED ESTIMATES & CABINS:\n`;
    text += `------------------------------------------\n`;

    Object.keys(families).forEach(famKey => {
      const fam = families[famKey];
      const sum = familyTotals[famKey];
      text += `- ${fam.name}:\n`;
      text += `  • Cabins: ${fam.rooms.map(r => r.toUpperCase()).join(' & ')}\n`;
      text += `  • Flight Mode: ${fam.flightMode.toUpperCase()}\n`;
      text += `  • Est. Cost: $${Math.round(sum).toLocaleString()}\n`;
    });

    text += `\n==========================================\n`;
    text += `Projected Grand Cruise Total: $${Math.round(grandTotal).toLocaleString()}\n`;
    text += `Avg. per Person: $${Math.round(grandTotal / totalAttendees).toLocaleString()} (${totalAttendees} total attendees)\n\n`;
    text += `Generated from Family Cruise Command Center Dashboard. Let's make it happen! 🌴🍹`;

    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }).catch(() => {
      alert("Clipboard copy failed. Try copying manually.");
    });
  };

  const spec = cruiseSpecs[activeShip];

  return (
    <SiteWrapper>
      <div className="main-page-wrapper">
        <Box sx={{ minHeight: "100vh", bgcolor: 'background.default', py: 4 }}>
          <Container maxWidth="xl">
            
            {/* Page Header */}
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), p: 1.5, borderRadius: 3, color: 'primary.main', display: 'flex' }}>
                  <ShipIcon sx={{ fontSize: 36 }} />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight="800" sx={{ letterSpacing: '-0.5px' }}>
                    Family Cruise Coordinator
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Summer 2027 Cruise Planning & Budget Matrix
                  </Typography>
                </Box>
              </Box>

              {/* Countdown timer */}
              <Paper 
                elevation={0} 
                sx={{ 
                  py: 1, px: 3, 
                  borderRadius: 3, 
                  bgcolor: alpha(theme.palette.text.primary, 0.03), 
                  border: '1px solid',
                  borderColor: 'divider',
                  display: 'flex', gap: 2, textAlign: 'center'
                }}
              >
                <Box>
                  <Typography variant="h6" fontWeight="800" color="primary.main">{countdownText.days}</Typography>
                  <Typography variant="caption" color="text.secondary">DAYS</Typography>
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight="800" color="primary.main">{countdownText.hours}</Typography>
                  <Typography variant="caption" color="text.secondary">HRS</Typography>
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight="800" color="primary.main">{countdownText.mins}</Typography>
                  <Typography variant="caption" color="text.secondary">MINS</Typography>
                </Box>
              </Paper>
            </Box>

            {/* Main Layout Grid */}
            <Grid container spacing={4}>
              
              {/* Left Column - Ship Selector & Spec cards */}
              <Grid item xs={12} lg={4.5}>
                
                {/* Ship Toggle Card */}
                <Card sx={{ borderRadius: 4, mb: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ExploreIcon color="primary" /> Select Active Comparison
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Button
                          fullWidth
                          variant={activeShip === 'icon' ? 'contained' : 'outlined'}
                          onClick={() => setActiveShip('icon')}
                          sx={{ 
                            py: 2, 
                            flexDirection: 'column', 
                            borderRadius: 3,
                            borderColor: activeShip === 'icon' ? 'primary.main' : 'divider',
                            bgcolor: activeShip === 'icon' ? 'primary.main' : 'transparent',
                            color: activeShip === 'icon' ? '#fff' : 'text.primary',
                            '&:hover': {
                              bgcolor: activeShip === 'icon' ? 'primary.dark' : alpha(theme.palette.text.primary, 0.03)
                            }
                          }}
                        >
                          <Typography variant="h5" sx={{ mb: 0.5 }}>🛳️</Typography>
                          <Typography variant="subtitle2" fontWeight="bold">Icon of the Seas</Typography>
                          <Typography variant="caption" opacity={0.8}>Sails Saturday</Typography>
                        </Button>
                      </Grid>
                      <Grid item xs={6}>
                        <Button
                          fullWidth
                          variant={activeShip === 'star' ? 'contained' : 'outlined'}
                          onClick={() => setActiveShip('star')}
                          sx={{ 
                            py: 2, 
                            flexDirection: 'column', 
                            borderRadius: 3,
                            borderColor: activeShip === 'star' ? 'primary.main' : 'divider',
                            bgcolor: activeShip === 'star' ? 'primary.main' : 'transparent',
                            color: activeShip === 'star' ? '#fff' : 'text.primary',
                            '&:hover': {
                              bgcolor: activeShip === 'star' ? 'primary.dark' : alpha(theme.palette.text.primary, 0.03)
                            }
                          }}
                        >
                          <Typography variant="h5" sx={{ mb: 0.5 }}>✨</Typography>
                          <Typography variant="subtitle2" fontWeight="bold">Star of the Seas</Typography>
                          <Typography variant="caption" opacity={0.8}>Sails Sunday</Typography>
                        </Button>
                      </Grid>
                    </Grid>

                    {/* Ship Details Card */}
                    <Box sx={{ mt: 3 }}>
                      <Box 
                        sx={{ 
                          height: 180, 
                          borderRadius: 3, 
                          overflow: 'hidden', 
                          position: 'relative',
                          border: '1px solid',
                          borderColor: 'divider',
                          mb: 2
                        }}
                      >
                        <Box 
                          component="img" 
                          src={spec.photo} 
                          alt={spec.name}
                          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, bgcolor: 'rgba(0,0,0,0.6)', p: 1.5, color: '#fff' }}>
                          <Typography variant="subtitle1" fontWeight="bold">{spec.name}</Typography>
                          <Typography variant="caption">{spec.desc}</Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}>
                          <Typography variant="body2" color="text.secondary">Homeport</Typography>
                          <Typography variant="body2" fontWeight="bold">{spec.homeport}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}>
                          <Typography variant="body2" color="text.secondary">Cruising Week</Typography>
                          <Typography variant="body2" fontWeight="bold">{spec.dates}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}>
                          <Typography variant="body2" color="text.secondary">Itinerary Stops</Typography>
                          <Typography variant="body2" fontWeight="bold">7-Night Caribbean</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', pb: 1 }}>
                          <Typography variant="body2" color="text.secondary">Ship Highlight</Typography>
                          <Typography variant="body2" fontWeight="bold" color="primary.main">{spec.highlight}</Typography>
                        </Box>
                      </Box>

                      {/* Itinerary Track */}
                      <Paper variant="outlined" sx={{ mt: 2, p: 2, bgcolor: alpha(theme.palette.text.primary, 0.01), borderRadius: 3 }}>
                        <Typography variant="subtitle2" color="primary.main" fontWeight="bold" sx={{ mb: 1.5 }}>
                          Itinerary Schedule:
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {spec.ports.map((p, i) => (
                            <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                              <Typography variant="caption" sx={{ minWidth: 42, color: 'text.secondary', fontWeight: 'bold' }}>
                                {p.day}:
                              </Typography>
                              <Typography variant="caption" fontWeight={p.highlight ? "bold" : "normal"}>
                                <b>{p.port}</b> — {p.action}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Paper>
                    </Box>
                  </CardContent>
                </Card>

                {/* Side-by-Side Static comparison table */}
                <Card sx={{ borderRadius: 4, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                      Icon vs Star Comparison
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider', pb: 1, fontSize: '0.85rem' }}>
                        <Typography variant="body2" fontWeight="bold">Departure Day</Typography>
                        <Typography variant="body2">Sat (Icon) vs <b>Sun (Star)</b></Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider', pb: 1, fontSize: '0.85rem' }}>
                        <Typography variant="body2" fontWeight="bold">Travel Buffer</Typography>
                        <Typography variant="body2" color="success.main">Star has Sunday buffer</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider', pb: 1, fontSize: '0.85rem' }}>
                        <Typography variant="body2" fontWeight="bold">Sailing Stops</Typography>
                        <Typography variant="body2">St. Maarten vs St. Kitts</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', pb: 1, fontSize: '0.85rem' }}>
                        <Typography variant="body2" fontWeight="bold">Ship Age</Typography>
                        <Typography variant="body2">2024 vs <b>2025/2026 (Newer)</b></Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

              </Grid>

              {/* Right Column - Interactive Family Planner & Budget summary */}
              <Grid item xs={12} lg={7.5}>
                
                {/* Family Cards */}
                <Card sx={{ borderRadius: 4, mb: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PeopleIcon color="primary" /> Family Room & Package Estimator
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {Object.keys(families).map(famKey => {
                        const fam = families[famKey];
                        const cost = familyTotals[famKey];
                        const occupancies = getRoomOccupancies(famKey);
                        const isLeah = famKey === 'leah';

                        return (
                          <Paper 
                            key={famKey} 
                            variant="outlined" 
                            sx={{ 
                              p: 3, 
                              borderRadius: 4,
                              borderColor: isLeah ? alpha(colors.leah, 0.4) : 'divider',
                              bgcolor: isLeah ? alpha(colors.leah, 0.02) : 'transparent',
                            }}
                          >
                            
                            {/* Family header cost row */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                              <Box>
                                <Typography variant="subtitle1" fontWeight="bold">{fam.name}</Typography>
                                <Chip 
                                  label={`${fam.attendees} Attendees (${fam.adults}A, ${fam.kids}K)`} 
                                  size="small" 
                                  variant="outlined" 
                                  sx={{ mt: 0.5, fontSize: '0.75rem', height: 20 }} 
                                />
                              </Box>
                              <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="caption" color="text.secondary" display="block">Estimated Cost</Typography>
                                <Typography variant="h5" fontWeight="800" color="primary.main">
                                  ${Math.round(cost).toLocaleString()}
                                </Typography>
                              </Box>
                            </Box>

                            <Divider sx={{ mb: 2.5 }} />

                            {/* Options fields */}
                            <Grid container spacing={3}>
                              
                              {/* Rooms column */}
                              <Grid item xs={12} md={4.5}>
                                <Typography variant="caption" fontWeight="bold" color="text.secondary" display="block" sx={{ mb: 1, textTransform: 'uppercase' }}>
                                  Stateroom Layout
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                  {fam.rooms.map((roomType, idx) => (
                                    <Box key={idx}>
                                      <FormControl fullWidth size="small">
                                        <InputLabel>Room {idx+1} Cabin</InputLabel>
                                        <Select
                                          value={roomType}
                                          label={`Room ${idx+1} Cabin`}
                                          onChange={(e) => handleRoomChange(famKey, idx, e.target.value)}
                                        >
                                          <MenuItem value="interior">Interior Cabin</MenuItem>
                                          <MenuItem value="oceanview">Oceanview Cabin</MenuItem>
                                          <MenuItem value="balcony">Balcony Cabin</MenuItem>
                                          <MenuItem value="suite">Luxury Suite</MenuItem>
                                        </Select>
                                      </FormControl>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, pl: 0.5 }}>
                                        Occupants: {occupancies[idx] || 2} passengers
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              </Grid>

                              {/* Toggles Addons Column */}
                              <Grid item xs={12} md={3.5}>
                                <Typography variant="caption" fontWeight="bold" color="text.secondary" display="block" sx={{ mb: 1, textTransform: 'uppercase' }}>
                                  Add-on Packages
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                  <FormControlLabel
                                    control={
                                      <Switch 
                                        size="small"
                                        checked={fam.addons.drinks} 
                                        onChange={(e) => handleAddonToggle(famKey, 'drinks', e.target.checked)} 
                                      />
                                    }
                                    label={<Typography variant="body2">Adult Deluxe Drinks</Typography>}
                                  />
                                  {fam.kids > 0 && (
                                    <FormControlLabel
                                      control={
                                        <Switch 
                                          size="small"
                                          checked={fam.addons.soda} 
                                          onChange={(e) => handleAddonToggle(famKey, 'soda', e.target.checked)} 
                                        />
                                      }
                                      label={<Typography variant="body2">Kids Soda ({fam.kids})</Typography>}
                                    />
                                  )}
                                  <FormControlLabel
                                    control={
                                      <Switch 
                                        size="small"
                                        checked={fam.addons.wifi} 
                                        onChange={(e) => handleAddonToggle(famKey, 'wifi', e.target.checked)} 
                                      />
                                    }
                                    label={<Typography variant="body2">Wi-Fi Internet</Typography>}
                                  />
                                  <FormControlLabel
                                    control={
                                      <Switch 
                                        size="small"
                                        checked={fam.addons.excursions} 
                                        onChange={(e) => handleAddonToggle(famKey, 'excursions', e.target.checked)} 
                                      />
                                    }
                                    label={<Typography variant="body2">Excursions budget</Typography>}
                                  />
                                </Box>
                              </Grid>

                              {/* Flight Settings Column */}
                              <Grid item xs={12} md={4}>
                                <Typography variant="caption" fontWeight="bold" color="text.secondary" display="block" sx={{ mb: 1, textTransform: 'uppercase' }}>
                                  Transit & Flights
                                </Typography>
                                <Paper 
                                  variant="outlined" 
                                  sx={{ 
                                    p: 1.5, 
                                    borderRadius: 3, 
                                    bgcolor: alpha(theme.palette.text.primary, 0.01),
                                    borderColor: isLeah ? alpha(theme.palette.primary.main, 0.2) : 'divider'
                                  }}
                                >
                                  <FormControl fullWidth size="small">
                                    <Select
                                      value={fam.flightMode}
                                      onChange={(e) => handleFlightModeChange(famKey, e.target.value)}
                                    >
                                      <MenuItem value="none">Driving (No Flight cost)</MenuItem>
                                      <MenuItem value="commercial">Commercial Flight ($350/p)</MenuItem>
                                      <MenuItem value="d2">D2 Non-Rev Standby ($0/p)</MenuItem>
                                      <MenuItem value="d2p">D2P Parent Standby ($50/p)</MenuItem>
                                      <MenuItem value="d3">D3 Buddy Standby ($100/p)</MenuItem>
                                    </Select>
                                  </FormControl>

                                  {/* Non-rev saving visual indicator */}
                                  {(fam.flightMode === 'd2' || fam.flightMode === 'd2p' || fam.flightMode === 'd3') && (
                                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main' }}>
                                      <CheckCircleIcon sx={{ fontSize: 14 }} />
                                      <Typography variant="caption" fontWeight="bold">
                                        Non-Rev standby active!
                                      </Typography>
                                    </Box>
                                  )}
                                </Paper>
                              </Grid>

                            </Grid>
                          </Paper>
                        );
                      })}
                    </Box>
                  </CardContent>
                </Card>

                {/* Combined Total Summary Card */}
                <Card sx={{ borderRadius: 4, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                  <CardContent sx={{ p: 4 }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ReceiptIcon color="primary" /> Grand Summary
                    </Typography>

                    <Grid container spacing={3} alignItems="center">
                      
                      {/* Big numbers */}
                      <Grid item xs={12} md={6}>
                        <Paper 
                          elevation={0} 
                          sx={{ 
                            p: 3, 
                            borderRadius: 4, 
                            bgcolor: alpha(theme.palette.text.primary, 0.03), 
                            textAlign: 'center',
                            border: '1px solid',
                            borderColor: 'divider'
                          }}
                        >
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                            Total Projected Cruise Cost
                          </Typography>
                          <Typography variant="h3" fontWeight="800" color="warning.main" sx={{ my: 1 }}>
                            ${Math.round(grandTotal).toLocaleString()}
                          </Typography>
                          <Typography variant="subtitle2" color="primary.main">
                            Avg. ${Math.round(grandTotal / totalAttendees).toLocaleString()} per person ({totalAttendees} attendees)
                          </Typography>
                        </Paper>
                      </Grid>

                      {/* Visual segment progress bar and voting */}
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary" fontWeight="bold" sx={{ mb: 1 }}>
                          Family Cost Share Distribution:
                        </Typography>

                        {/* Cost distribution bar */}
                        <Box sx={{ display: 'flex', height: 16, borderRadius: 8, overflow: 'hidden', border: '1px solid', borderColor: 'divider', mb: 2 }}>
                          {Object.keys(families).map(famKey => {
                            const val = familyTotals[famKey];
                            const pct = grandTotal > 0 ? (val / grandTotal) * 100 : 0;
                            return (
                              <Tooltip key={famKey} title={`${families[famKey].name}: ${Math.round(pct)}%`}>
                                <Box sx={{ width: `${pct}%`, bgcolor: colors[famKey], height: '100%' }} />
                              </Tooltip>
                            );
                          })}
                        </Box>

                        <Grid container spacing={1} sx={{ mb: 3 }}>
                          {Object.keys(families).map(famKey => (
                            <Grid item xs={6} key={famKey} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: colors[famKey] }} />
                              <Typography variant="caption" color="text.secondary">
                                {famKey === 'grandparents' ? 'Grandparents' : famKey === 'tom' ? "Tom's" : famKey === 'wendy' ? "Wendy's" : "Leah's"} ({Math.round(grandTotal > 0 ? (familyTotals[famKey] / grandTotal)*100 : 0)}%)
                              </Typography>
                            </Grid>
                          ))}
                        </Grid>

                        <Divider sx={{ my: 2 }} />

                        {/* Alignment voting choices */}
                        <Typography variant="body2" color="text.secondary" fontWeight="bold" sx={{ mb: 1 }}>
                          Preferred Ship for Clipboard copy:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                          <Button 
                            size="small" 
                            variant={voteShip === 'icon' ? 'contained' : 'outlined'} 
                            onClick={() => setVoteShip('icon')}
                          >
                            Icon of the Seas
                          </Button>
                          <Button 
                            size="small" 
                            variant={voteShip === 'star' ? 'contained' : 'outlined'} 
                            onClick={() => setVoteShip('star')}
                          >
                            Star of the Seas
                          </Button>
                        </Box>

                        <Button
                          fullWidth
                          variant="contained"
                          color="primary"
                          startIcon={<ContentCopyIcon />}
                          onClick={copyPlanToClipboard}
                          sx={{ py: 1.5, borderRadius: 3, fontWeight: 'bold' }}
                        >
                          Copy Planning Summary for Text/iMessage
                        </Button>
                        {copied && (
                          <Typography variant="caption" color="success.main" align="center" display="block" sx={{ mt: 1, fontWeight: 'bold' }}>
                            Copied successfully!
                          </Typography>
                        )}
                      </Grid>

                    </Grid>
                  </CardContent>
                </Card>

              </Grid>

            </Grid>
          </Container>
        </Box>
      </div>
    </SiteWrapper>
  );
};

export default CruisePlanning;
